/**
 * articleExtractor — readable article body out of a raw news page.
 * --------------------------------------------------------------------------
 * Input:  the HTML of an article page (as fetched by the /api/article proxy).
 * Output: an ordered list of typed blocks the reader renders directly.
 *
 * Why not DOMParser + innerHTML
 * -----------------------------
 *   1. Security. The output of this module is third-party HTML from six sites
 *      we do not control. Producing a *whitelisted block model* instead of a
 *      markup string means the reader never needs dangerouslySetInnerHTML, so
 *      there is no injection surface at all — not "a sanitised one", none.
 *   2. Portability. The app's component contracts are kept React-Native
 *      portable, and RN has no DOM. So does the Node test suite. A string
 *      parser runs in all four environments (browser, WebView, RN, Node)
 *      unchanged; DOMParser runs in two.
 *
 * The algorithm is the well-known readability heuristic, reduced to what these
 * six sites actually need:
 *
 *   Pass 1  walk the document, score every plausible container by how much
 *           <p> text it holds, ignoring navigation/promo subtrees entirely.
 *   Pass 2  re-walk the winning container and emit blocks in document order.
 *
 * Both passes share one tolerant tokeniser. It never throws on malformed
 * markup: stray closing tags are ignored, unclosed tags are closed implicitly
 * at the end of their parent.
 */

import { cleanHtml, collapseWhitespace, resolveUrl } from './htmlText.js';

/** Elements that never have a closing tag. */
const VOID_TAGS = new Set([
  'area', 'base', 'br', 'col', 'embed', 'hr', 'img', 'input',
  'link', 'meta', 'param', 'source', 'track', 'wbr',
]);

/** Subtrees that hold no article prose under any circumstances. */
const NOISE_TAGS = new Set([
  'script', 'style', 'noscript', 'svg', 'iframe', 'form', 'template',
  'nav', 'header', 'footer', 'aside', 'button', 'select', 'video', 'audio',
]);

/**
 * Words that mark a subtree as furniture. Matched against the class+id of
 * every element after normalising it to lower-case space-separated words, so
 * `article__share-bar`, `relatedArticles` and `related_articles` all reduce to
 * something these word-boundary patterns can see.
 *
 * Kept deliberately conservative — a false positive here silently deletes
 * paragraphs from the middle of an article, which is far worse than leaving a
 * "Related stories" strip in. `header`/`footer` are absent on purpose: the
 * TAGS are already noise, but `entry-header` routinely holds the standfirst.
 */
const JUNK_PATTERN =
  /\b(share|sharing|related|promo|advert|advertisement|ads?|newsletter|subscribe|subscription|comments?|cookie|consent|banner|sidebar|breadcrumbs?|byline|metadata|trending|recommended|popular|nav|navigation|menu|pagination|paywall|widget|toolbar|read more|more from|most read|skip link|author (info|bio|box|card|avatar|description|heading|title|meta)|social (share|sharing|links?|icons?|buttons?|follow|bar))\b/;

/**
 * Junk words safe to match ACROSS a camelCase boundary.
 *
 * Splitting camelCase is what lets us catch `relatedContent__info`, but doing
 * it with the full list above is what made `ContainerWithSidebar` look like a
 * sidebar. So the camel pass uses only words that cannot plausibly turn up
 * inside a legitimate content-container name — no layout vocabulary
 * (sidebar/banner/nav/menu/widget), only vocabulary that names furniture
 * outright.
 */
const STRICT_JUNK_PATTERN =
  /\b(share|sharing|related|advert|advertisement|newsletter|subscribe|subscription|comments?|paywall|breadcrumbs?|byline|recommended|trending|metadata)\b/;

/**
 * Headings that mark the end of the article and the start of the page's
 * "what to read next" tail. Everything from here down is dropped.
 */
const FURNITURE_HEADING =
  /^(related|more on this|more from|read more|share or save|share this|most read|recommended|comments?|follow us|published by|sign up|subscribe|advertisement|topics?|tags?)\b/i;

/** BBC prefixes its figure captions with a visually-hidden label. */
const CAPTION_LABEL = /^(image|video|media|figure)\s+(caption|source)\s*,\s*/i;

/** Containers worth scoring as the article body. */
const CANDIDATE_TAGS = new Set(['article', 'main', 'div', 'section']);

/** class/id substrings that make a container more likely to be the body. */
const BODY_HINT = /(article|content|post|entry|story|body|main|prose|rich-?text)/;

/** Tag → emitted block type. Anything not listed contributes text only. */
const BLOCK_TAGS = {
  p: 'paragraph',
  h1: 'heading',
  h2: 'heading',
  h3: 'heading',
  h4: 'heading',
  li: 'listItem',
  blockquote: 'quote',
  figcaption: 'caption',
  pre: 'code',
};

const HEADING_LEVEL = { h1: 2, h2: 2, h3: 3, h4: 3 };

/** Block types that absorb nested block tags instead of being split by them. */
const WRAPPING_BLOCKS = new Set(['quote', 'listItem', 'caption']);

/** Anything shorter than this is a caption fragment, a label, or a stray. */
const MIN_BLOCK_CHARS = 2;

/** Below this the "article" is a paywall stub or a cookie wall, not prose. */
const MIN_ARTICLE_WORDS = 60;

/** Runaway guard for pathological pages. */
const MAX_BLOCKS = 500;

const TOKEN_RE =
  /<!--[\s\S]*?-->|<(\/?)([a-zA-Z][a-zA-Z0-9:-]*)((?:"[^"]*"|'[^']*'|[^>])*?)\/?>/g;

/**
 * Elements whose content is raw text, not markup. HTML says a parser must run
 * to the literal closing tag without interpreting anything between.
 *
 * This is not a nicety. Minified inline JavaScript is full of `<` — `for(;A<d;)`
 * tokenises as an opening <d> tag — and because the phantom tag then swallows
 * the real </script>, the element stack never unwinds. Every <script> on the
 * page permanently increments the noise depth, and from the first one onward
 * the whole document is treated as furniture. Measured on RaceFans: zero
 * paragraphs extracted from a 2,700-word article.
 */
const RAW_TEXT_TAGS = new Set(['script', 'style', 'noscript', 'template', 'textarea', 'title']);

/**
 * tokenise — yields {type:'text'|'open'|'close', ...} in document order.
 *
 * `start`/`end` are character offsets into `html`; pass 1 uses them to slice
 * out the winning container without having to build a tree.
 */
function* tokenise(html) {
  TOKEN_RE.lastIndex = 0;
  let cursor = 0;
  let match;

  while ((match = TOKEN_RE.exec(html)) !== null) {
    if (match.index > cursor) {
      yield { type: 'text', value: html.slice(cursor, match.index) };
    }
    cursor = TOKEN_RE.lastIndex;

    // Comment — matched by the first alternative, so group 2 is undefined.
    if (match[2] === undefined) continue;

    const tag = match[2].toLowerCase();

    if (match[1] === '/') {
      yield { type: 'close', tag, start: match.index };
      continue;
    }

    if (RAW_TEXT_TAGS.has(tag)) {
      // Emit the element as an empty, self-contained noise node and resume
      // after its closing tag. An unterminated one runs to end of document.
      const closeRe = new RegExp(`</${tag}\\s*>`, 'i');
      const rest = html.slice(cursor);
      const closeAt = rest.search(closeRe);
      const contentEnd = closeAt === -1 ? html.length : cursor + closeAt;

      yield { type: 'open', tag, attrs: match[3] ?? '', end: cursor };
      yield { type: 'close', tag, start: contentEnd };

      cursor = closeAt === -1 ? html.length : contentEnd + rest.match(closeRe)[0].length;
      TOKEN_RE.lastIndex = cursor;
      continue;
    }

    yield { type: 'open', tag, attrs: match[3] ?? '', end: TOKEN_RE.lastIndex };
  }

  if (cursor < html.length) {
    yield { type: 'text', value: html.slice(cursor) };
  }
}

/** Pull one attribute out of a raw attribute string. */
function attr(attrs, name) {
  const re = new RegExp(`(?:^|\\s)${name}\\s*=\\s*("([^"]*)"|'([^']*)'|([^\\s>]+))`, 'i');
  const m = re.exec(attrs);
  if (!m) return '';
  return m[2] ?? m[3] ?? m[4] ?? '';
}

/**
 * classSignature — class + id reduced to lower-case space-separated words,
 * splitting on separators (`-`, `_`, whitespace) ONLY.
 *
 * Deliberately NOT camelCase-aware. Splitting `ContainerWithSidebar` into
 * "container with sidebar" makes `\bsidebar\b` match BBC's outermost article
 * wrapper, and the hard junk filter then discards the entire page — measured:
 * every BBC article extracted zero words. Publishers put junk words inside
 * compound component names constantly, so a fragment match is not evidence.
 *
 * The trade is that `relatedArticles` is missed where `related-articles` is
 * caught. That asymmetry is the right way round: a missed junk block leaves a
 * "Related stories" strip at the end of an otherwise correct article, while a
 * false positive deletes the article.
 */
function rawSignature(attrs) {
  if (!attrs) return '';
  // data-testid / data-component are the semantic hooks that survive on sites
  // whose class names are build-hashed — BBC labels its metadata strip
  // `data-testid="metadata"` and nothing else on the element says so.
  return [
    attr(attrs, 'class'),
    attr(attrs, 'id'),
    attr(attrs, 'data-testid'),
    attr(attrs, 'data-component'),
  ]
    .join(' ')
    .trim();
}

function classSignature(attrs) {
  return rawSignature(attrs).replace(/[^a-zA-Z0-9]+/g, ' ').toLowerCase().trim();
}

/** True when an element's class/id marks it as page furniture. */
function isJunk(attrs) {
  const raw = rawSignature(attrs);
  if (!raw) return false;

  const separated = raw.replace(/[^a-zA-Z0-9]+/g, ' ').toLowerCase().trim();
  if (JUNK_PATTERN.test(separated)) return true;

  // Second pass: split camelCase apart too, matched against the narrower list.
  const camel = raw
    .replace(/([a-z0-9])([A-Z])/g, '$1 $2')
    .replace(/[^a-zA-Z0-9]+/g, ' ')
    .toLowerCase()
    .trim();
  return camel === separated ? false : STRICT_JUNK_PATTERN.test(camel);
}

/**
 * popTo — tolerant close handling. Finds the nearest matching open frame and
 * pops everything above it (those tags were never closed). A close tag with no
 * matching open is ignored entirely rather than corrupting the stack.
 */
function popTo(stack, tag) {
  for (let i = stack.length - 1; i >= 0; i--) {
    if (stack[i].tag === tag) return stack.splice(i);
  }
  return null;
}

/**
 * findArticleBody — pass 1. Returns {html, score} for the best container, or
 * null when nothing on the page looks like prose.
 */
function findArticleBody(html) {
  const stack = [];
  const candidates = [];
  let noiseDepth = 0;
  let paragraphDepth = 0;

  for (const token of tokenise(html)) {
    if (token.type === 'text') {
      // Only text inside a <p>, outside furniture, counts towards a score.
      if (noiseDepth === 0 && paragraphDepth > 0) {
        const length = collapseWhitespace(token.value).length;
        if (length > 0) {
          for (const frame of stack) {
            if (frame.candidate) frame.candidate.textLength += length;
          }
        }
      }
      continue;
    }

    if (token.type === 'open') {
      if (VOID_TAGS.has(token.tag)) continue;

      const noise = NOISE_TAGS.has(token.tag) || isJunk(token.attrs);
      let candidate = null;

      if (!noise && noiseDepth === 0 && CANDIDATE_TAGS.has(token.tag)) {
        candidate = { tag: token.tag, attrs: token.attrs, contentStart: token.end, textLength: 0 };
        candidates.push(candidate);
      }

      stack.push({ tag: token.tag, noise, candidate });
      if (noise) noiseDepth++;
      if (token.tag === 'p') paragraphDepth++;
      continue;
    }

    // close
    const popped = popTo(stack, token.tag);
    if (!popped) continue;
    for (const frame of popped) {
      if (frame.noise) noiseDepth--;
      if (frame.tag === 'p') paragraphDepth--;
      // Implicitly-closed frames get the same end offset; harmless, since a
      // container that never closed extends to wherever its parent ended.
      if (frame.candidate) frame.candidate.contentEnd = token.start;
    }
  }

  // Unclosed containers run to the end of the document.
  for (const candidate of candidates) {
    if (candidate.contentEnd === undefined) candidate.contentEnd = html.length;
  }

  let best = null;
  for (const candidate of candidates) {
    if (candidate.textLength === 0) continue;

    // Prefer the *innermost* container holding the text: every ancestor has at
    // least as much <p> text, so raw length alone always picks <body>. Charging
    // for the markup wrapped around the prose favours the tight one.
    const span = candidate.contentEnd - candidate.contentStart;
    const density = candidate.textLength / Math.max(span, 1);

    let score = candidate.textLength * (0.25 + density);
    if (candidate.tag === 'article') score *= 1.6;
    if (BODY_HINT.test(classSignature(candidate.attrs))) score *= 1.35;

    if (!best || score > best.score) best = { score, candidate };
  }

  if (!best) return null;
  return {
    html: html.slice(best.candidate.contentStart, best.candidate.contentEnd),
    score: best.score,
  };
}

/**
 * collectBlocks — pass 2. Walks the article container and emits typed blocks
 * in document order.
 */
function collectBlocks(fragment, baseUrl) {
  const blocks = [];
  const stack = [];
  let noiseDepth = 0;
  /** Innermost open block; text accumulates here. */
  let open = null;

  const flush = () => {
    if (!open) return;
    let text = cleanHtml(open.buffer);
    if (open.type === 'caption') text = text.replace(CAPTION_LABEL, '').trim();
    if (text.length >= MIN_BLOCK_CHARS) {
      const block = { type: open.type, text };
      if (open.level) block.level = open.level;
      blocks.push(block);
    }
    open = null;
  };

  for (const token of tokenise(fragment)) {
    if (blocks.length >= MAX_BLOCKS) break;

    if (token.type === 'text') {
      if (open && noiseDepth === 0) open.buffer += token.value;
      continue;
    }

    if (token.type === 'open') {
      if (token.tag === 'br') {
        if (open) open.buffer += ' ';
        continue;
      }

      if (token.tag === 'img') {
        if (noiseDepth === 0 && !open) {
          // `src` is often a 1px placeholder on lazy-loading sites; the real
          // asset hides in data-src / the first srcset entry.
          const raw =
            attr(token.attrs, 'data-src') ||
            attr(token.attrs, 'src') ||
            attr(token.attrs, 'srcset').split(',')[0]?.trim().split(/\s+/)[0] ||
            '';
          const src = resolveUrl(raw, baseUrl);
          if (src) blocks.push({ type: 'image', src, alt: cleanHtml(attr(token.attrs, 'alt')) });
        }
        continue;
      }

      if (VOID_TAGS.has(token.tag)) continue;

      const noise = NOISE_TAGS.has(token.tag) || isJunk(token.attrs);
      stack.push({ tag: token.tag, noise, openedBlock: false });
      if (noise) {
        // Furniture can appear mid-article ("more from this author"). Whatever
        // block was in progress ends here rather than absorbing it.
        noiseDepth++;
        flush();
        continue;
      }

      const type = BLOCK_TAGS[token.tag];
      if (!type || noiseDepth > 0) continue;

      // A <p> inside a <blockquote>, <li> or <figcaption> belongs to that
      // block — it is not a new paragraph. Without this, BBC's captions split
      // into an empty "Image caption," block plus a body paragraph carrying
      // the caption text, which then reads as article prose.
      //
      // The separator matters: markup is not guaranteed to have whitespace
      // between </p> and <p>, and without it a two-paragraph pull quote
      // renders as "First line.Second line."
      if (open && WRAPPING_BLOCKS.has(open.type) && open.type !== type) {
        open.buffer += ' ';
        continue;
      }

      flush();
      open = { type, buffer: '', level: HEADING_LEVEL[token.tag], tag: token.tag };
      stack[stack.length - 1].openedBlock = true;
      continue;
    }

    // close
    const popped = popTo(stack, token.tag);
    if (!popped) continue;
    for (const frame of popped) {
      if (frame.noise) noiseDepth--;
      if (frame.openedBlock) flush();
    }
  }

  flush();
  return blocks;
}

/**
 * dropDuplicates — sites repeat the headline as an <h1> inside the body, and
 * standfirst text often appears twice (once for mobile, once for desktop).
 */
function dropDuplicates(blocks, title) {
  const normalise = (s) => s.toLowerCase().replace(/[^a-z0-9]+/g, ' ').trim();
  const titleKey = title ? normalise(title) : null;
  const seen = new Set();
  const out = [];

  for (const block of blocks) {
    if (block.type === 'image') {
      if (seen.has(block.src)) continue;
      seen.add(block.src);
      out.push(block);
      continue;
    }
    const key = normalise(block.text);
    if (!key) continue;
    if (titleKey && key === titleKey) continue;
    // Only long blocks are deduped globally — short ones ("Advertisement",
    // list bullets, repeated sub-headings) legitimately recur.
    if (key.length > 40) {
      if (seen.has(key)) continue;
      seen.add(key);
    }
    out.push(block);
  }

  return out;
}

/**
 * trimTail — remove the "what to read next" furniture every publisher appends.
 *
 * Two passes, both conservative enough to survive a redesign:
 *
 *   1. Cut at the last furniture heading ("Related topics", "Share Or Save
 *      This Story", "Published by"), but only if it falls in the back half of
 *      the article — an article may legitimately open with a "More from this
 *      series" style sub-heading.
 *   2. Then drop trailing images and captions. A bare image after the closing
 *      paragraph is a promo tile or a next-article thumbnail, never content.
 *
 * Deliberately NOT "drop everything after the last long paragraph": plenty of
 * legitimate articles close on a list (session times, vital statistics).
 */
function trimTail(blocks) {
  let end = blocks.length;

  // First furniture heading in the back half — not the last. Cutting at the
  // last one leaves every earlier tail section ("Related topics" above "More
  // on this story") sitting in the article.
  for (let i = Math.floor(blocks.length / 2); i < blocks.length; i++) {
    const block = blocks[i];
    if (block.type === 'heading' && FURNITURE_HEADING.test(block.text)) {
      end = i;
      break;
    }
  }

  while (end > 0 && (blocks[end - 1].type === 'image' || blocks[end - 1].type === 'caption')) {
    end--;
  }

  return blocks.slice(0, end);
}

function countWords(blocks) {
  return blocks
    .filter((b) => b.type !== 'image')
    .reduce((total, b) => total + b.text.split(/\s+/).filter(Boolean).length, 0);
}

/**
 * extractArticle — public entry point.
 *
 * @param {string} html      raw article page
 * @param {object} options
 * @param {string} [options.baseUrl]  page URL, for resolving relative images
 * @param {string} [options.title]    known headline, so we can drop its echo
 * @returns {{blocks: Array, wordCount: number, ok: boolean}}
 *          `ok` is false when the page yielded too little prose to be worth
 *          showing — a paywall, a consent interstitial, or a redirect stub.
 */
export function extractArticle(html, { baseUrl, title } = {}) {
  if (!html || typeof html !== 'string') {
    return { blocks: [], wordCount: 0, ok: false };
  }

  const body = findArticleBody(html);
  if (!body) return { blocks: [], wordCount: 0, ok: false };

  const blocks = trimTail(dropDuplicates(collectBlocks(body.html, baseUrl), title));
  const wordCount = countWords(blocks);

  return { blocks, wordCount, ok: wordCount >= MIN_ARTICLE_WORDS };
}

/**
 * blocksFromHtmlFragment — for feed payloads that already carry the full body
 * in <content:encoded> (The Race does). Same block model, no container search:
 * the fragment IS the article.
 */
export function blocksFromHtmlFragment(fragment, { baseUrl, title } = {}) {
  if (!fragment || typeof fragment !== 'string') {
    return { blocks: [], wordCount: 0, ok: false };
  }
  const blocks = trimTail(dropDuplicates(collectBlocks(fragment, baseUrl), title));
  const wordCount = countWords(blocks);
  return { blocks, wordCount, ok: wordCount >= MIN_ARTICLE_WORDS };
}

/** Reading time in minutes, at the usual 200 wpm. */
export function readingMinutes(wordCount) {
  return Math.max(1, Math.min(Math.ceil(wordCount / 200), 60));
}
